/**
 * MEDIS v2 implementation of `GetPatient` / `RegisterPatient`.
 * Routing (which calls use v2 and where they go) lives in `src/lib/medis/v2/client.ts`.
 *
 * v2 `GET /v2/GetPatient`:
 *   - auth: Azure EasyAuth `X-ZUMO-AUTH` token (handled by `medisV2Api`)
 *   - body: the inner `Response` of `getPatientResponse` (PascalCase fields),
 *     read case-insensitively via the helpers in `@/lib/medis/v2`.
 *   - 204 No Content when the account has no registered patient yet.
 *
 * v2 `POST /v2/RegisterPatient`:
 *   - same `{ ssnHash, uniqueCode, birthYear, attachPatient }` body as v1
 *   - soft business failures (InvalidCode, AlreadyAttached, …) arrive as
 *     HTTP 400 *with* a body
 *   - register mode (`attachPatient: false`) also returns `AttachedAt` — the
 *     accounts the patient was already attached to before being registered.
 *
 * v2 `POST /v2/DetachPatient`:
 *   - `{ patientToDetach: <FhirID>, detachFromAccount: <email from AttachedBy> }`
 *   - 400 when the email is not in the patient's `AttachedBy`
 *   - ⚠️ still under construction server-side — currently answers 501 on the
 *     happy path; wired up anyway so it lights up when the server ships it.
 *
 * v2 `GET /v2/GetPatientRefresh` (v2 only):
 *   - `{ UniqueCode, AttachedBy[] }` of the primary patient — the attach code
 *     and the accounts it is attached to — without loading the whole list
 *   - 204 No Content when the account has no registered patient
 *   - called on every entry to the patients page; the result is merged into
 *     the cached patient list (see `usePatientRefresh`)
 */
import { MedisError } from '@/lib/medis/envelope';
import {
  medisV2Api,
  MEDIS_V2_ROUTES,
  v2Url,
  unwrapV2,
  prop,
  asArray,
  parseV2Date,
  debugV2Shape,
  type DetachPatientRequestV2,
  type DetachPatientResponseV2,
  type GetPatientRefreshResponseV2,
  type GetPatientResponseV2,
  type PatientDetailV2,
  type RegisterPatientResponseV2,
} from '@/lib/medis/v2';
import { toAddPatientResult } from '../lib/registerOutcome';
import type {
  AddPatientResult,
  DetachPatientInput,
  DetachPatientResult,
  Patient,
  PatientRefresh,
} from '../types/patient.types';

function mapPatientV2(dto: PatientDetailV2, isPrimary: boolean): Patient | null {
  const fhirId = prop<string>(dto, 'FhirID');
  if (!fhirId) return null; // a patient without a FHIR id can't be used downstream

  const firstName = prop<string>(dto, 'FirstName') ?? '';
  const lastName = prop<string>(dto, 'LastName') ?? '';
  const medisId = prop<number>(dto, 'MedisID');
  const age = prop<string | number>(dto, 'Age');

  return {
    fhirId,
    medisId: medisId ?? null,
    firstName,
    lastName,
    fullName: [firstName, lastName].filter(Boolean).join(' ').trim(),
    birthDay: parseV2Date(prop<string>(dto, 'BirthDay')),
    age: String(age ?? ''),
    isPrimary,
    attachedBy: prop<string[]>(dto, 'AttachedBy') ?? [],
    actCode: prop<string>(dto, 'ActCode') ?? null,
  };
}

/** Lists the account holder plus any attached dependents, via the v2 API. */
export async function getPatientsV2(): Promise<Patient[]> {
  const route = MEDIS_V2_ROUTES.getPatient;
  if (!route) throw new Error('v2 getPatient route is disabled');

  const { status, data } = await medisV2Api.get(await v2Url(route));
  // 204 No Content — the account has no registered patient yet.
  if (status === 204) return [];

  const body = unwrapV2<GetPatientResponseV2>(data);
  debugV2Shape('GetPatient', body);

  if (prop<boolean>(body, 'Errored')) {
    throw new MedisError('Cererea către serviciul medical a eșuat.', 'error');
  }

  const patients: Patient[] = [];

  const primaryDto = prop<PatientDetailV2>(body, 'Patient');
  if (primaryDto) {
    const primary = mapPatientV2(primaryDto, true);
    if (primary) patients.push(primary);
  }

  for (const attached of asArray<PatientDetailV2>(prop(body, 'PatientsAttached'))) {
    const mapped = mapPatientV2(attached, false);
    if (mapped) patients.push(mapped);
  }

  return patients;
}

/**
 * Re-reads the primary patient's attach code + `AttachedBy` via the v2 API.
 *
 * Returns `null` when there is nothing fresh to apply — 204 (no registered
 * patient) or a 200 whose body carries neither field — so the caller keeps
 * the cached values; an unrecognised payload must never wipe the cached list.
 * Transport / HTTP failures reject (axios default `validateStatus`), which the
 * caller treats the same way.
 */
export async function getPatientRefreshV2(): Promise<PatientRefresh | null> {
  const route = MEDIS_V2_ROUTES.getPatientRefresh;
  if (!route) throw new Error('v2 getPatientRefresh route is disabled');

  const { status, data } = await medisV2Api.get(await v2Url(route));
  if (status === 204) return null;

  const body = unwrapV2<GetPatientRefreshResponseV2>(data);
  debugV2Shape('GetPatientRefresh', body);

  const rawCode = prop<string>(body, 'UniqueCode');
  const rawAttachedBy = prop<unknown>(body, 'AttachedBy');
  if (rawCode === undefined && rawAttachedBy === undefined) return null;

  const uniqueCode = String(rawCode ?? '').trim();
  return {
    uniqueCode: uniqueCode || null,
    attachedBy: asArray<unknown>(rawAttachedBy)
      .map((a) => String(a ?? '').trim())
      .filter(Boolean),
  };
}

/** Registers (`attachPatient: false`) or attaches a patient, via the v2 API. */
export async function registerPatientV2(payload: {
  ssnHash: string;
  uniqueCode: string;
  birthYear: number;
  attachPatient: boolean;
}): Promise<AddPatientResult> {
  const route = MEDIS_V2_ROUTES.registerPatient;
  if (!route) throw new Error('v2 registerPatient route is disabled');

  // Accept 400 (soft business failure with a body); let 401 reject so the
  // ZUMO refresh interceptor can retry the call.
  const { data } = await medisV2Api.post(await v2Url(route), payload, {
    validateStatus: (s) => (s >= 200 && s < 300) || s === 400,
  });
  const body = unwrapV2<RegisterPatientResponseV2>(data);
  debugV2Shape('RegisterPatient', body);

  return toAddPatientResult(
    {
      success: prop<boolean>(body, 'Success'),
      outcome: prop<string>(body, 'Outcome'),
      username: prop<string>(body, 'Username'),
      attachedAt: asArray<string>(prop(body, 'AttachedAt')),
    },
    payload.attachPatient,
  );
}

/**
 * Removes a patient↔account association. Exactly one identifier is sent,
 * depending on the direction (`leaveAccount` → `detachFromAccount`,
 * `detachDependent` → `patientToDetach`); the server infers the other side from
 * the authenticated session.
 */
export async function detachPatientV2(input: DetachPatientInput): Promise<DetachPatientResult> {
  const route = MEDIS_V2_ROUTES.detachPatient;
  if (!route) throw new Error('v2 detachPatient route is disabled');

  const payload: DetachPatientRequestV2 =
    input.mode === 'leaveAccount'
      ? { detachFromAccount: input.account }
      : { patientToDetach: input.patientFhirId };

  // Accept 400 (validation, with a body) and 501 (endpoint not live yet) so we
  // can surface friendly outcomes; let 401 reject for the ZUMO refresh retry.
  const { status, data } = await medisV2Api.post(await v2Url(route), payload, {
    validateStatus: (s) => (s >= 200 && s < 300) || s === 400 || s === 501,
  });
  const body = unwrapV2<DetachPatientResponseV2>(data);
  debugV2Shape('DetachPatient', body);

  const message = prop<string>(body, 'Message') ?? undefined;
  if (status === 501) return { success: false, outcome: 'notImplemented', message };
  if (status === 400) return { success: false, outcome: 'invalid', message };
  // 2xx — treat as detached unless the (not-yet-final) body explicitly denies it.
  if (prop<boolean>(body, 'Success') === false) return { success: false, outcome: 'error', message };
  return { success: true, outcome: 'detached', message };
}
